import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED, LEGAL_LAST_UPDATED_AR } from "@/lib/legal";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Lumvio" },
      {
        name: "description",
        content:
          "How Lumvio collects, uses, and protects personal information across its verified freelancer marketplace.",
      },
      { property: "og:title", content: "Privacy Policy — Lumvio" },
      { property: "og:description", content: "How Lumvio handles personal information." },
      { property: "og:url", content: "https://lumvio.co/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (
    <LegalPageLayout title={ar ? "سياسة الخصوصية" : "Privacy Policy"} updated={ar ? LEGAL_LAST_UPDATED_AR : LEGAL_LAST_UPDATED}>
      {ar ? <ArabicBody /> : <EnglishBody />}
    </LegalPageLayout>
  );
}

function EnglishBody() {
  return (
    <>
      <p>
        This Privacy Policy explains how Lumvio ("we", "our", "us") collects, uses, discloses, and protects personal
        information when you use our website, applications, and services (the "Service"). This Policy is maintained by
        Lumvio.
      </p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li>
          <strong>Account data:</strong> name, email, role (freelancer or client), password hash.
        </li>
        <li>
          <strong>Identity data:</strong> government-issued ID and selfie processed by our verification provider (Stripe
          Identity). We store the verification result and a session reference, not the raw document images.
        </li>
        <li>
          <strong>Profile and work data:</strong> avatar, bio, skills, portfolio, project briefs, messages, files
          uploaded to workspaces.
        </li>
        <li>
          <strong>Transaction data:</strong> protected milestones, invoices, wallet balances, payout method metadata (no
          full card numbers).
        </li>
        <li>
          <strong>Technical data:</strong> IP address, device and browser info, cookies, and log events.
        </li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>Provide, secure, and improve the Service.</li>
        <li>Verify identity and prevent fraud, abuse, and money laundering.</li>
        <li>Process payments and release protected milestones.</li>
        <li>Communicate about your account, transactions, and product updates.</li>
        <li>Comply with legal obligations.</li>
      </ul>

      <h2>3. Legal Bases</h2>
      <p>
        Where required (e.g. under the EU/UK GDPR), we rely on the following bases: performance of a contract,
        compliance with legal obligations, legitimate interests (security and product improvement), and consent (where
        specifically requested).
      </p>

      <h2>4. Sharing</h2>
      <p>We share personal information with:</p>
      <ul>
        <li>
          <strong>Service providers</strong> such as Stripe (identity and payments) and Supabase (managed database and
          storage).
        </li>
        <li>
          <strong>Other users</strong> to the extent needed to perform contracts you enter into (e.g., your name is
          shown to your counterparty).
        </li>
        <li>
          <strong>Authorities</strong> when required by law or to protect rights, safety, and the integrity of the
          Service.
        </li>
      </ul>
      <p>We do not sell personal information.</p>

      <h2>5. International Transfers</h2>
      <p>
        Data may be processed in countries other than your own. Where required, we use appropriate safeguards such as
        Standard Contractual Clauses.
      </p>

      <h2>6. Retention</h2>
      <p>
        We keep account and transaction data for as long as your account is active and afterwards as needed to comply
        with legal, tax, and dispute-resolution obligations.
      </p>

      <h2>7. Your Rights</h2>
      <p>
        Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, or port your personal
        information, and to object to certain processing. To exercise these rights, email{" "}
        <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>. You may also lodge a complaint with your local data
        protection authority.
      </p>

      <h2>8. Security</h2>
      <p>
        We implement technical and organisational measures including encryption in transit, row-level access controls,
        and least-privilege administrative access. No system is perfectly secure; we cannot guarantee absolute security.
      </p>

      <h2>9. Cookies</h2>
      <p>
        We use strictly necessary cookies for authentication and session management, and limited analytics cookies to
        understand usage. You can control cookies through your browser settings.
      </p>

      <h2>10. Children</h2>
      <p>The Service is not directed to children under 18 and we do not knowingly collect their data.</p>

      <h2>11. Changes</h2>
      <p>We may update this Policy from time to time. Material changes will be notified through the Service.</p>

      <h2>12. Contact</h2>
      <p>
        Questions may be sent to <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <p>
        توضح سياسة الخصوصية هذه كيف تجمع Lumvio ("نحن"، "لنا"، "الخاصة بنا") المعلومات الشخصية وتستخدمها وتُفصح عنها
        وتحميها عند استخدامك لموقعنا وتطبيقاتنا وخدماتنا ("الخدمة"). تتولى Lumvio صيانة هذه السياسة.
      </p>

      <h2>1. المعلومات التي نجمعها</h2>
      <ul>
        <li>
          <strong>بيانات الحساب:</strong> الاسم، البريد الإلكتروني، الدور (مستقل أو عميل)، وتجزئة كلمة المرور.
        </li>
        <li>
          <strong>بيانات الهوية:</strong> بطاقة الهوية الحكومية والصورة الذاتية اللتان يعالجهما مزود التحقق لدينا
          (Stripe Identity). نحتفظ بنتيجة التحقق ومرجع الجلسة فقط، وليس بصور الوثائق الأصلية.
        </li>
        <li>
          <strong>بيانات الملف الشخصي والعمل:</strong> الصورة الرمزية، النبذة، المهارات، معرض الأعمال، ملخصات المشاريع،
          الرسائل، والملفات المرفوعة إلى مساحات العمل.
        </li>
        <li>
          <strong>بيانات المعاملات:</strong> مراحل حماية المدفوعات، الفواتير، أرصدة المحفظة، البيانات الوصفية لطريقة الدفع (بدون
          أرقام البطاقات الكاملة).
        </li>
        <li>
          <strong>البيانات التقنية:</strong> عنوان IP، ومعلومات الجهاز والمتصفح، وملفات تعريف الارتباط، وأحداث السجل.
        </li>
      </ul>

      <h2>2. كيف نستخدم المعلومات</h2>
      <ul>
        <li>تقديم الخدمة وتأمينها وتحسينها.</li>
        <li>التحقق من الهوية ومنع الاحتيال وسوء الاستخدام وغسل الأموال.</li>
        <li>معالجة المدفوعات والإفراج عن مراحل حماية المدفوعات.</li>
        <li>التواصل بشأن حسابك ومعاملاتك وتحديثات المنتج.</li>
        <li>الامتثال للالتزامات القانونية.</li>
      </ul>

      <h2>3. الأسس القانونية</h2>
      <p>
        حيثما يُشترط ذلك (مثلاً بموجب اللائحة العامة لحماية البيانات في الاتحاد الأوروبي/المملكة المتحدة)، نعتمد على
        الأسس التالية: تنفيذ عقد، والامتثال للالتزامات القانونية، والمصالح المشروعة (الأمن وتحسين المنتج)، والموافقة
        (حيث يُطلب ذلك تحديدًا).
      </p>

      <h2>4. المشاركة</h2>
      <p>نشارك المعلومات الشخصية مع:</p>
      <ul>
        <li>
          <strong>مزوّدي الخدمة</strong> مثل Stripe (الهوية والمدفوعات) و Supabase (قاعدة البيانات والتخزين المُدارَين).
        </li>
        <li>
          <strong>المستخدمين الآخرين</strong> بالقدر اللازم لتنفيذ العقود التي تُبرمها (مثلاً، يظهر اسمك للطرف المقابل).
        </li>
        <li>
          <strong>السلطات</strong> عند اقتضاء القانون ذلك أو لحماية الحقوق والسلامة وسلامة الخدمة.
        </li>
      </ul>
      <p>لا نبيع المعلومات الشخصية.</p>

      <h2>5. عمليات النقل الدولية</h2>
      <p>
        قد تُعالَج البيانات في دول غير دولتك. حيثما يلزم، نستخدم ضمانات مناسبة مثل البنود التعاقدية القياسية.
      </p>

      <h2>6. الاحتفاظ بالبيانات</h2>
      <p>
        نحتفظ ببيانات الحساب والمعاملات طوال فترة نشاط حسابك وبعد ذلك بالقدر اللازم للامتثال للالتزامات القانونية
        والضريبية والمتعلقة بحل النزاعات.
      </p>

      <h2>7. حقوقك</h2>
      <p>
        بحسب اختصاصك القضائي، قد يكون لك الحق في الوصول إلى معلوماتك الشخصية أو تصحيحها أو حذفها أو تقييد معالجتها أو
        نقلها، والاعتراض على معالجات معينة. لممارسة هذه الحقوق، راسل{" "}
        <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>. يمكنك أيضًا تقديم شكوى إلى هيئة حماية البيانات
        المحلية.
      </p>

      <h2>8. الأمن</h2>
      <p>
        نطبّق تدابير تقنية وتنظيمية تشمل التشفير أثناء النقل، وضوابط الوصول على مستوى الصف (RLS)، والوصول الإداري بأقل
        الامتيازات. لا يوجد نظام آمن تمامًا؛ لذلك لا يمكننا ضمان الأمان المطلق.
      </p>

      <h2>9. ملفات تعريف الارتباط (Cookies)</h2>
      <p>
        نستخدم ملفات تعريف الارتباط الضرورية للمصادقة وإدارة الجلسات، وملفات تحليلات محدودة لفهم الاستخدام. يمكنك
        التحكم في ملفات تعريف الارتباط من إعدادات المتصفح.
      </p>

      <h2>10. الأطفال</h2>
      <p>الخدمة غير موجهة للأطفال دون 18 عامًا ولا نجمع بياناتهم عن علم.</p>

      <h2>11. التغييرات</h2>
      <p>قد نُحدّث هذه السياسة من وقت لآخر. يتم الإخطار بالتغييرات الجوهرية عبر الخدمة.</p>

      <h2>12. التواصل</h2>
      <p>
        يمكن إرسال الأسئلة إلى <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}
